import Logo from "./Logo";
import { DEMO_URL, LEGAL_LINKS, PLATFORM_URL, SITE, TAGLINE, footerLinks } from "./brand";

/**
 * HRMLESS V3 footer — an ink CTA band above a white directory body.
 *
 * Ported from `app/src/components/Footer.tsx`. Copy is
 * carried over unchanged (the public site runs a strict copy guard), and every
 * link matches it, CTA buttons and columns alike. The demo CTA links out to the
 * marketing site's form rather than opening a modal, because this app does not
 * carry that form.
 *
 * As with SiteHeader, the tokens arrive as the --hl-* custom properties from
 * config/theme.tsx rather than as the public site's Tailwind token classes.
 */

const columns = [
  { head: "Products", links: footerLinks.product },
  { head: "Industries", links: footerLinks.industries },
  { head: "Resources", links: footerLinks.resources },
  { head: "Company", links: footerLinks.company },
] as const;

// Two deliberate lines (design comment); the break exists at md+ only.
const ctaHeadingLines = [
  "Your best people already set the bar.",
  "See what HRMLESS does with it.",
];

export default function SiteFooter() {
  return (
    <>
      <style>{footerCSS}</style>
      <div className="hl-footer">
        {/* Conversion CTA band (ink) — V3's recurring end-of-page band */}
        <section className="hl-footer__cta">
          <div className="hl-footer__cta-inner">
            <h2 className="hl-footer__cta-heading">
              {ctaHeadingLines.map((line, i) => (
                <span key={line}>
                  {i > 0 && <br className="hl-footer__cta-break" />}
                  {line}
                </span>
              ))}
            </h2>
            <div className="hl-footer__cta-actions">
              <a
                href={DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hl-btn hl-btn--blue hl-btn--lg"
              >
                Request a demo
              </a>
              <a
                href={PLATFORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hl-btn hl-btn--ghost-dark hl-btn--lg"
              >
                Explore platform
              </a>
            </div>
          </div>
        </section>

        {/* Directory body (white) */}
        <div className="hl-footer__body">
          <div className="hl-footer__body-inner">
            <div className="hl-footer__grid">
              {/* Brand column — design: logo + tagline only. */}
              <div className="hl-footer__brand">
                <a href={SITE} className="hl-footer__brand-logo" aria-label="HRMLESS home">
                  <Logo variant="dark" height="32px" />
                </a>
                <p className="hl-footer__tagline">{TAGLINE}</p>
              </div>

              {columns.map((col) => (
                <div key={col.head} className="hl-footer__col">
                  <h4 className="hl-footer__col-head">{col.head}</h4>
                  <div className="hl-footer__col-links">
                    {col.links.map((link) => (
                      <a
                        key={link.href}
                        href={link.href}
                        {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                        className="hl-footer__link"
                      >
                        {link.name}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom bar — design: © left, legal links right */}
            <div className="hl-footer__bottom">
              <span>&copy; {new Date().getFullYear()} HRMLESS Software</span>
              <div className="hl-footer__legal">
                <a href={LEGAL_LINKS.privacy} target="_blank" rel="noopener noreferrer">Privacy Notice</a>
                <a href={LEGAL_LINKS.terms} target="_blank" rel="noopener noreferrer">Terms of Service</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const footerCSS = `
.hl-footer {
  /* Break out of Zudoku's max-width container to span the full viewport. */
  width: 100vw;
  position: relative;
  left: 50%;
  transform: translateX(-50%);
  overflow-x: hidden;
  font-family: var(--hl-font-body);
}
.hl-footer * { box-sizing: border-box; }

/* ── CTA band (ink) ────────────────────────────────────────────── */
.hl-footer__cta {
  background: var(--hl-ink);
  color: var(--hl-text-on-dark);
}
.hl-footer__cta-inner {
  max-width: 1240px;
  margin: 0 auto;
  padding: 96px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 32px;
}
.hl-footer__cta-heading {
  margin: 0;
  max-width: 960px;
  font-family: var(--hl-font-display);
  font-weight: 500;
  font-size: 34px;
  line-height: 1.15;
  letter-spacing: -0.5px;
  color: var(--hl-text-on-dark);
}
.hl-footer__cta-break { display: none; }
.hl-footer__cta-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

/* ── Directory body (page surface) ─────────────────────────────── */
.hl-footer__body {
  background: var(--hl-surface-page);
  border-top: 1px solid var(--hl-border-soft);
}
.hl-footer__body-inner {
  max-width: 1240px;
  margin: 0 auto;
  padding: 72px 24px 48px;
}
.hl-footer__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;
}
.hl-footer__brand {
  grid-column: span 2;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.hl-footer__brand-logo { display: inline-block; text-decoration: none; }
.hl-footer__tagline {
  margin: 0;
  max-width: 280px;
  font-size: 15px;
  line-height: 1.6;
  color: var(--hl-text-muted);
}
.hl-footer__col { display: flex; flex-direction: column; gap: 16px; }
.hl-footer__col-head {
  margin: 0;
  font-family: var(--hl-font-body);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--hl-text-muted);
}
.hl-footer__col-links { display: flex; flex-direction: column; gap: 10px; }
.hl-footer__link {
  font-family: var(--hl-font-ui);
  font-size: 15px;
  line-height: 1.6;
  color: var(--hl-text-primary);
  text-decoration: none;
  transition: color var(--hl-duration-fast) ease;
}
.hl-footer__link:hover { color: var(--hl-green-600); }

.hl-footer__bottom {
  margin-top: 56px;
  padding-top: 28px;
  border-top: 1px solid var(--hl-border-soft);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  font-size: 14px;
  color: var(--hl-text-muted);
}
.hl-footer__legal { display: flex; gap: 24px; }
.hl-footer__legal a {
  color: inherit;
  text-decoration: none;
  transition: color var(--hl-duration-fast) ease;
}
.hl-footer__legal a:hover { color: var(--hl-text-primary); }

@media (min-width: 768px) {
  .hl-footer__cta-inner { padding: 128px 40px; }
  .hl-footer__cta-heading { font-size: 48px; }
  .hl-footer__cta-break { display: inline; }
  .hl-footer__body-inner { padding: 72px 40px 48px; }
  .hl-footer__grid { grid-template-columns: repeat(5, 1fr); gap: 48px; }
  .hl-footer__brand { grid-column: span 1; }
  .hl-footer__bottom { flex-direction: row; justify-content: space-between; }
}

@media (min-width: 1720px) {
  .hl-footer__cta-inner, .hl-footer__body-inner { max-width: 1540px; }
}
`;
